import type { CSSProperties, Ref } from 'vue'
import type { ColumnDef, Row, Column, VisibilityState } from '@tanstack/table-core'
import { type TableOptions } from '@tanstack/vue-table'
import type { TranslatedFields } from '@/interfaces/Language.interfaces'
import type { Pagination } from '@/interfaces/Pagination.interfaces'

export type DatatableRow<T extends object> = Row<T>
export type DatatableColumn<T extends object> = Column<T>
export type DatatableColumnDef<T extends object> = ColumnDef<T>
export type DatatableOptions<T extends object> = TableOptions<T>

export type DatatableColumnConfig<T extends object> = Pick<ColumnDef<T>, 'header' | 'cell'> & {
  id: string
  enableSorting?: boolean
  align?: DatatableAlign
  isActionsCell?: boolean
  enableHiding?: boolean
  isMainCell?: boolean
  stickyCol?: StickyColSide
  style?: CSSProperties
}

export enum DatatableAlign {
  RIGHT = 'right',
  CENTER = 'center',
  LEFT = 'left',
}

export enum StickyColSide {
  RIGHT = 'right',
  LEFT = 'left',
}

export enum DatatableSortingOrderBy {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface DatatableSorting {
  columnId: string
  orderBy: DatatableSortingOrderBy
}

export interface DatatablePagination {
  value: number
  numberOfPages: number
  disabled?: boolean
}

export enum DatatableBackground {
  TRANSPARENT = 'transparent',
  NEUTRAL = 'neutral',
  WHITE = 'white',
}

export enum NdsDatatableCellCountVariant {
  LIGHT = 'light',
  WARNING = 'warning',
}

export interface NdsDatatableState {
  columnVisibility: Ref<VisibilityState>
}

export interface DatatableGroupByBase<T extends object> {
  groups: DatatableGroupByData<T>[]
  paging: DatatableGroupByPaging
}

export interface DatatableGroupByData<T extends object> {
  uid: string
  items: T[]
  groups: DatatableGroupByGroup[]
  total: number
}

export interface DatatableGroupByGroup {
  id?: number
  value: string | number | object | null
  label: TranslatedFields
  type?: string
}

export interface DatatableGroupByPaging {
  page_count: number
  count: number
  groups_count: number
}

export interface DatatableGroupState {
  page: number
  collapsed: boolean
  loading: boolean
}

export type DatatableGroupsState = Record<string, DatatableGroupState>

export interface DatatableGroupByOptions<G extends string> {
  group: G
  limit_per_group: number
}

export interface DatatableGroupByFetchParams<G extends string> {
  pagination: Pagination
  // Absent on a request scoped to a single group, as the API would group the scoped group again
  groupBy?: DatatableGroupByOptions<G>
  // Groups of the scoped group, to filter the request on
  scopeGroups?: DatatableGroupByGroup[]
}

export interface UseDatatableGroupByOptions<T extends object, R extends DatatableGroupByBase<T>, G extends string> {
  fetch: (params: DatatableGroupByFetchParams<G>) => Promise<R | null>
  groupBy: Ref<G | undefined>
  perPage?: number
  perGroup?: number
  state?: Ref<DatatableGroupsState>
}

export interface DatatableStickyHeaderOptions {
  isStickyHeaderActive: boolean
  offsetTop: number
  setStickyHeaderColWidths?: () => void
  stickyHeaderWrapper: Ref<HTMLElement | null>
  stickyTrigger: Ref<HTMLElement | null>
}
